// Modules
import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button, FormGroup, Icon, InputGroup, Intent } from '@blueprintjs/core'
import axios from 'axios'

// Components
import Logo from 'components/brand/logo/Logo'
import Page from 'components/layout/page/Page'

// Hooks
import useLanguage from 'hooks/system/useLanguage'
import useNotices from 'hooks/system/useNotices'
import useVerification from 'hooks/authentication/useVerification'

// Helpers
import { getURLSearchParameter, resetURLSearchParameters } from 'helpers/route'
import { convertFontSizeToPixelSize } from 'helpers/size'

// Constants
import { EMAIL_VERIFICATION_CODE_URL } from 'constants/environment'
import { CONFIRMATION_CODE_MINIMUM_LENGTH } from 'constants/settings'

// Styles
import './Form.scss'

// Section: Verification > Landing > Form
function Form () {
	// State
	const [formData, setFormData] = useState({ code: '', email: getURLSearchParameter('email') })
	const [isCodeResent, setIsCodeResent] = useState(false)
	const [isInitialized, setIsInitialized] = useState(false)

	// Data
	const navigate = useNavigate()
	const { addNotice } = useNotices()
	const { translate } = useLanguage('pages.verification.views.landing.sections.form')
	const { isProcessing, verify } = useVerification()
	const isFormReady = formData.code.length >= CONFIRMATION_CODE_MINIMUM_LENGTH

	// Functions
	function handleInput (type, value) {
		setFormData(formData => ({ ...formData, [type]: value }))
	}
	function handleSubmit (event) {
		event.preventDefault()
		if (isFormReady) verify(formData)
	}
	async function resendCode () {
		const { email } = formData
		try {
			await axios({ data: { email }, method: 'POST', url: EMAIL_VERIFICATION_CODE_URL })
			setIsCodeResent(true)
		} catch (error) {
			let code = error?.response?.data?.message ? `server.${error?.response?.data?.message}` : 'client.emailVerificationCodeResendingError'
			addNotice({ code, log: error, type: 'error' })
		}
	}

	// Effects
	useEffect(() => {
		resetURLSearchParameters()
		setTimeout(() => setIsInitialized(true), 1000)
	}, [])
	useEffect(() => {
		let timer
		if (isCodeResent) timer = setTimeout(() => setIsCodeResent(false), 2000)
		return () => clearTimeout(timer)
	}, [isCodeResent])

	// Render
	if (!formData.email) navigate('/login') // TODO: Also do this if it's not an email address
	return (
		<Page.Section
			willGrow
			willCenter
			name="form">
			<div className={
				'aix-authentication-form' +
				(isInitialized ? ' aix-initialized' : '')
			}>
				<Logo
					className="aix-logo"
					isCommonColor={!isInitialized} />
				<div className="aix-content">
					<form className="aix-form aix-paper-thick">
						<h2 className="aix-title">{translate.local('form.title')}</h2>
						<FormGroup>
							<InputGroup
								disabled
								name="email"
								type="email"
								value={formData.email}
								className="aix-input aix-email" />
						</FormGroup>
						<FormGroup>
							<InputGroup
								disabled={isProcessing}
								name="code"
								type="text"
								value={formData.code}
								className="aix-input aix-code"
								placeholder={translate.local('form.fields.code.placeholder')}
								onChange={({ target }) => handleInput('code', target.value)} />
							<div className="aix-option">
								{isCodeResent
									? <>
										<Icon
											icon="tick"
											intent={Intent.SUCCESS}
											size={convertFontSizeToPixelSize('.5625rem')} />
										{translate.local('form.options.resendCode.label.success')}
									</>
									: <a onClick={resendCode}>{translate.local('form.options.resendCode.label.idle')}</a>
								}
							</div>
						</FormGroup>
						<Button
							fill
							disabled={!isFormReady || isProcessing}
							loading={isProcessing}
							className="aix-submit bp4-large"
							onClick={handleSubmit}
							type="submit">
							{translate.local('form.actions.verifyAccount')}
						</Button>
					</form>
				</div>
			</div>
		</Page.Section>
	)
}

// Export
export default Form
